import type { ProjectRecord } from "@/types/api";

/**
 * Resolve the record handed to the open [[image-viewer-modal.spec]] from the
 * *live* records list rather than the snapshot captured when the viewer opened.
 *
 * [[records-view]] keeps the tapped record in local state (`selectedRecord`),
 * set once at tile-tap. That snapshot goes stale the moment the record's data
 * changes underneath it — most visibly when a background append lands and the
 * record's `images[]` gains the synced photo (see [[append-photo-outbox]]).
 * Re-resolving the open record by id against the records query — the same list
 * the grid and prev/next navigation already read — makes the open viewer reflect
 * those changes the same render they hit the cache, exactly as the modal already
 * resolves a pending record's image source reactively rather than from the frozen
 * snapshot.
 *
 * Falls back to the captured `selected` when it is absent from the live list — a
 * pending/offline draft (whose id is a client uuid the server list never carries)
 * or a record filtered out of the current view — so the viewer never blanks.
 * Returns null when nothing is open.
 */
export function resolveOpenRecord(
  selected: ProjectRecord | null,
  liveRecords: readonly ProjectRecord[],
): ProjectRecord | null {
  if (!selected) return null;
  return liveRecords.find((r) => r.id === selected.id) ?? selected;
}
